import { AsyncPipe } from '@angular/common';
import { Component, OnInit, inject, ViewChild, ViewContainerRef, AfterViewInit } from '@angular/core';
import { DrawerComponent, KENDO_LAYOUT } from '@progress/kendo-angular-layout';
import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, map, switchMap, take, tap } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';

import { PageComponent } from './layout/components/page/page.component';
import { SideMenuComponent } from './layout/components/side-menu/side-menu.component';
import { SideMenuStateActions } from './layout/components/side-menu/store/side-menu-state.actions';
import { selectIsMenuStateLoaded, selectMenuState } from './layout/components/side-menu/store/side-menu-state.reducer';
import { ComponentMediaObserverService } from './services/utility/component-media-observer.service';
import { Breakpoints } from './services/utility/media-observer.types';
import { widthMap } from './shared/constants/side-menu.constants';
import { TranslateModule } from '@ngx-translate/core';
import { ModalService } from './services/utility/modal.service';

@Component({
  selector: 'aos-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [ComponentMediaObserverService],
  imports: [SideMenuComponent, AsyncPipe, PageComponent, KENDO_LAYOUT, TranslateModule]
})
export class AppComponent implements AfterViewInit, OnInit {
  private readonly mediaObserver = inject(ComponentMediaObserverService);
  readonly #store = inject(Store);
  readonly #oidcService = inject(OidcSecurityService);

  @ViewChild('modalContainer', { read: ViewContainerRef })
  public modalContainerRef!: ViewContainerRef;

  protected readonly expanded$ = new BehaviorSubject(true);
  // TODO: Values of `isTablet$` are not used in the template. `isTablet$` is protected only for `AsyncPipe`.
  protected readonly isTablet$ = this.mediaObserver.hasBreakpoint(Breakpoints.L).pipe(
    filter(Boolean),
    tap(isTablet => {
      if (isTablet && this.expanded$.value) {
        this.expanded$.next(false);
        this.#store.dispatch(SideMenuStateActions.setHorizontalExpanded({ horizontalExpanded: false }));
      }
    })
  );
  protected readonly drawerWidth$ = this.mediaObserver.changes(Breakpoints.L, Breakpoints.XL, Breakpoints.XXL).pipe(
    map(changes => {
      const change = changes.filter(ch => ch.matches);
      return change.length ? widthMap[change[0].suffix as Breakpoints] : widthMap[Breakpoints.XXL];
    })
  );

  #modalService = inject(ModalService);

  ngOnInit(): void {
    this.#oidcService.isAuthenticated$
      .pipe(
        filter(({ isAuthenticated }) => isAuthenticated),
        take(1)
      )
      .subscribe(() => this.#store.dispatch(SideMenuStateActions.getMenuState()));

    this.#store
      .select(selectIsMenuStateLoaded)
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() => this.#store.select(selectMenuState).pipe(take(1)))
      )
      .subscribe(state => {
        const persisted = state?.horizontalExpanded;
        if (persisted === null || persisted === undefined) return;
        if (!persisted) {
          this.expanded$.next(false);
        } else {
          this.mediaObserver
            .hasBreakpoint(Breakpoints.L)
            .pipe(take(1))
            .subscribe(isTablet => {
              if (!isTablet) this.expanded$.next(true);
            });
        }
      });
  }

  ngAfterViewInit(): void {
    this.#modalService.containerRef.set(this.modalContainerRef);
  }

  protected toggleDrawer(drawer: DrawerComponent): void {
    drawer.toggle();
    const newValue = !this.expanded$.value;
    this.expanded$.next(newValue);
    this.#store.dispatch(SideMenuStateActions.setHorizontalExpanded({ horizontalExpanded: newValue }));
  }
}
